import { WarningAmberOutlined } from "@mui/icons-material";
import { Box, Button, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { mapClientToProfileFields } from "utils/clientProfileFields";
import { formatDateForInput } from "views/admin/Accounts/accountUtils";
import { fetchAdmissionAccountLinkExists } from "views/admin/Accounts/admissionSync";
import ModuleTasksTab from "components/Tasks/ModuleTasksTab";
import AdmissionAttachmentsTab from "./AdmissionAttachmentsTab";
import AdmissionNotesTab from "./AdmissionNotesTab";
import AdmissionStatusHistory from "./AdmissionStatusHistory";
import { getClientProfileCompleteness } from "./clientProfileCompleteness";
import GeneralInfo from "./GeneralInfo";
import { normalizeAdmissionAttachments } from "./normalizeAdmissionAttachments";
import { resolvePlacementSelection } from "./placementSelection";
import UniversityInfo from "./UniversityInfo";

const mergePlacementFields = (info, courseList) => {
  const { selectedCourse, selectedUniversity } = resolvePlacementSelection(courseList, info);
  return {
    ...info,
    selectedCourse: selectedCourse ?? info.selectedCourse,
    selectedUniversity: selectedUniversity ?? info.selectedUniversity,
  };
};

const TABS = [
  { id: "applicant", label: "Applicant details" },
  { id: "attachments", label: "Documents" },
  { id: "application", label: "Application form" },
  { id: "history", label: "Status history" },
  { id: "notes", label: "Notes" },
  { id: "tasks", label: "Tasks" },
];

const parseApplicationData = (jsonStr) => {
  if (!jsonStr) return {};
  try {
    return JSON.parse(jsonStr);
  } catch {
    return {};
  }
};

const applyClientToGeneralInfo = (prev, client) => {
  if (!client) {
    return { ...prev, clientId: prev?.clientId ?? null };
  }
  return {
    ...prev,
    ...mapClientToProfileFields(client),
    id: prev?.id,
    status: prev?.status || "Incomplete Application",
  };
};

const AddStudent = () => {
  const [
    { clientList, uniData, courseList },
    { refetchStudent, refetchUniData, refetchClient, refetchCourseList },
  ] = useKothar();
  const navigate = useNavigate();
  const fetchedIdRef = useRef(null);
  const hydratedClientIdRef = useRef(null);

  useEffect(() => {
    if (!uniData?.length) refetchUniData?.();
    if (!clientList?.length) refetchClient?.();
    if (!courseList?.length) refetchCourseList?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const stateHydratedIdRef = useRef(null);
  const preselectClientRef = useRef(false);

  const [activeTab, setActiveTab] = useState(0);

  const [generalInfo, setGeneralInfo] = useState({
    gender: null,
    clientId: null,
    studentId: null,
    maritalStatus: null,
    emergency: { name: "", email: "", contact: null, relation: null },
    course: null,
    university: null,
    state: null,
    intake: null,
    fee: null,
    caseOfficer: null,
    reference: null,
    status: "Incomplete Application",
    active: true,
    completeStatus: false,
    onShoreStatus: true,
  });
  const [addressInfo, setAddressInfo] = useState({
    permanent: { country: null, state: null, city: null, zip: null },
    temp: { country: null, state: null, city: null, zip: null },
    passport: { country: null, placeOfBirth: null },
    passportFile: null,
    moreThanOneCitizen: null,
    secondCountryName: "",
    livingInAnotherCountry: null,
    secondLivingCountryName: null,
    refusedFromAnyCountry: null,
    refusedCountryName: null,
    seriousMedicalCondition: null,
    medicalCause: null,
    criminalOffence: null,
    causeOfCriminalOffence: null,
    immigrationToOtherCountry: null,
    immigratedCountry: null,
  });
  const [academicInfo, setAcademicInfo] = useState({
    tenthDocuments: [],
    higherDocuments: [],
    bachelorDocuments: [],
    tenth: {
      board: null,
      gradingSystem: null,
      nameOfSchool: null,
      primaryMediumOfInstruction: null,
      countryOfStudy: null,
      stateOfStudy: null,
      score: null,
      scoreOutOf: null,
      passsOutYear: null,
      degree: null,
      degreeTitle: null,
    },
    higher: {
      board: null,
      gradingSystem: null,
      nameOfSchool: null,
      primaryMediumOfInstruction: null,
      countryOfStudy: null,
      stateOfStudy: null,
      score: null,
      scoreOutOf: null,
      passsOutYear: null,
      degree: null,
      degreeTitle: null,
    },
    bachelor: {
      board: null,
      gradingSystem: null,
      nameOfSchool: null,
      primaryMediumOfInstruction: null,
      countryOfStudy: null,
      stateOfStudy: null,
      score: null,
      scoreOutOf: null,
      passsOutYear: null,
      degree: null,
      degreeTitle: null,
    },
  });
  const [testInfo, setTestInfo] = useState({
    testName1: null,
    score1: null,
    uniqueId1: null,
    doe1: null,
    document1: null,
    speaking: null,
    listening: null,
    reading: null,
    writing: null,
    testName2: null,
    score2: null,
    uniqueId2: null,
    doe2: null,
    document2: null,
  });
  const [workInfo, setWorkInfo] = useState([
    { nameofOrg: null, from: null, to: null, docs: null, uid: crypto.randomUUID() },
  ]);
  const [attachments, setAttachments] = useState([]);
  const [clientLocked, setClientLocked] = useState(false);
  // Live-task badge for the Tasks tab. Kept out of generalInfo so it never leaks into saves.
  const [openAdmissionTaskCount, setOpenAdmissionTaskCount] = useState(0);
  const { id } = useParams();

  const admissionId = generalInfo?.id || id;

  // Re-reads just the badge after task changes (the form draft must not be overwritten).
  const refreshAdmissionTaskCount = () => {
    if (!admissionId) return;
    axios
      .get(`${API_URL}/student/${admissionId}`)
      .then(({ data }) => {
        const value = data?.data ?? data;
        setOpenAdmissionTaskCount(Number(value?.openTaskCount ?? 0));
      })
      .catch(() => {});
  };

  const { state } = useLocation();

  useEffect(() => {
    if (id || preselectClientRef.current) return;
    const clientId = state?.preselectClientId;
    if (!clientId) return;

    const client =
      state?.preselectClient || clientList?.find((c) => String(c.id) === String(clientId));

    if (!client) {
      if (clientList?.length) {
        preselectClientRef.current = true;
        setGeneralInfo((prev) => ({ ...prev, clientId: Number(clientId) || clientId }));
      }
      return;
    }

    preselectClientRef.current = true;
    setGeneralInfo((prev) => applyClientToGeneralInfo(prev, client));
  }, [id, state?.preselectClientId, state?.preselectClient, clientList]);

  useEffect(() => {
    if (state?.tab !== undefined) {
      setActiveTab(state.tab);
    }
  }, []);

  useEffect(() => {
    if (state) {
      const value = state?.item;
      if (!value?.id || stateHydratedIdRef.current === value.id) return;
      stateHydratedIdRef.current = value.id;
      const updatedClient = clientList?.find((item) => item?.id === value?.clientId);

      setGeneralInfo(
        mergePlacementFields(
          {
            id: value?.id,
            clientId: value?.clientId,
            studentId: value?.studentId || null,
            // Client fields are refreshed from the client record itself by the effect below;
            // the cached list is only a first paint and may not be loaded yet.
            ...mapClientToProfileFields(updatedClient),
            emergency: {
              name: value?.emergencyContactName ?? value?.emergency?.name,
              email: value?.emergencyEmail ?? value?.emergency?.email,
              contact: value?.emergencyContact ?? value?.emergency?.contact,
              relation: value?.emergencyRelation ?? value?.emergency?.relation,
            },
            course: value?.course,
            university: value?.university,
            state: value?.state,
            intake: value?.intake,
            enrollmentYear: value?.enrollmentYear,
            fee: value?.fee,
            status: value?.status,
            active: value?.active,
            completeStatus: value?.completeStatus,
            onShoreStatus: value?.onShoreStatus ?? true,
            courseStartDate: formatDateForInput(value?.courseStartDate),
            courseEndDate: formatDateForInput(value?.courseEndDate),
            clientAccountDetailId: value?.clientAccountDetailId ?? null,
            ...(() => {
              const appData = parseApplicationData(value?.applicationData);
              return {
                ...appData,
                applicationPhase: appData?.applicationPhase ?? 1,
                maxPhaseReached:
                  appData?.maxPhaseReached ??
                  (appData?.coeReceived
                    ? 4
                    : value?.courseEndDate
                    ? 3
                    : appData?.offerLetterApplied
                    ? 2
                    : 1),
              };
            })(),
          },
          courseList
        )
      );
      setAddressInfo((prev) => ({
        ...prev,
        permanent: value?.permanent,
        temp: value?.temp,
        passport: value?.passport,
        passportFile: value?.passportFile,
        moreThanOneCitizen: value?.moreThanOneCitizen,
        secondCountryName: value?.secondCountryName,
        livingInAnotherCountry: value?.livingInAnotherCountry,
        secondLivingCountryName: value?.secondLivingCountryName,
        refusedFromAnyCountry: value?.refusedFromAnyCountry,
        refusedCountryName: value?.refusedCountryName,
        seriousMedicalCondition: value?.seriousMedicalCondition,
        medicalCause: value?.medicalCause,
        criminalOffence: value?.criminalOffence,
        causeOfCriminalOffence: value?.causeOfCriminalOffence,
        immigrationToOtherCountry: value?.immigrationToOtherCountry,
        immigratedCountry: value?.immigratedCountry,
      }));
      setAcademicInfo(value.academicInfo);
      setTestInfo(value?.testInfo);
      setWorkInfo(
        value?.workInfo?.length
          ? value.workInfo
          : [{ nameofOrg: null, from: null, to: null, docs: null, uid: crypto.randomUUID() }]
      );
      setAttachments(normalizeAdmissionAttachments(value));
      setActiveTab(0);
    }
  }, [state, clientList, uniData, courseList]);

  // Fetch the full record by ID even when the list row was passed through navigation state.
  useEffect(() => {
    if (!id || fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;
    axios
      .get(`${API_URL}/student/${id}`)
      .then(({ data }) => {
        const value = data?.data ?? data;
        const updatedClient = clientList?.find((item) => item?.id === value?.clientId);
        const appData = parseApplicationData(value?.applicationData);
        setGeneralInfo(
          mergePlacementFields(
            {
              id: value?.id,
              name: value?.name,
              address: value?.address,
              dob: value?.dob,
              gender: value?.gender,
              email: value?.email,
              number: value?.number,
              passportNumber: value?.passportNumber,
              visaExpiry: value?.visaExpiry,
              visaStatus: value?.visaStatus,
              passportExpiry: value?.passportExpiry,
              dateOfIssue: value?.dateOfIssue,
              usiNumber: value?.usiNumber,
              maritalStatus: value?.maritalStatus,
              caseOfficer: value?.caseOfficer,
              reference: value?.reference,
              clientId: value?.clientId,
              studentId: value?.studentId || null,
              // Client fields are refreshed from the client record itself by the effect below;
              // the cached list is only a first paint and may not be loaded yet.
              ...mapClientToProfileFields(updatedClient),
              emergency: {
                name: value?.emergencyContactName ?? value?.emergency?.name,
                email: value?.emergencyEmail ?? value?.emergency?.email,
                contact: value?.emergencyContact ?? value?.emergency?.contact,
                relation: value?.emergencyRelation ?? value?.emergency?.relation,
              },
              course: value?.course,
              university: value?.university,
              state: value?.state,
              intake: value?.intake,
              enrollmentYear: value?.enrollmentYear,
              fee: value?.fee,
              status: value?.status,
              active: value?.active,
              completeStatus: value?.completeStatus,
              onShoreStatus: value?.onShoreStatus ?? true,
              courseStartDate: formatDateForInput(value?.courseStartDate),
              courseEndDate: formatDateForInput(value?.courseEndDate),
              clientAccountDetailId: value?.clientAccountDetailId ?? null,
              ...appData,
              applicationPhase: appData?.applicationPhase ?? 1,
              maxPhaseReached:
                appData?.maxPhaseReached ??
                (appData?.coeReceived
                  ? 4
                  : value?.courseEndDate
                  ? 3
                  : appData?.offerLetterApplied
                  ? 2
                  : 1),
            },
            courseList
          )
        );
        setAddressInfo((prev) => ({
          ...prev,
          permanent: value?.permanent,
          temp: value?.temp,
          passport: value?.passport,
          passportFile: value?.passportFile,
          moreThanOneCitizen: value?.moreThanOneCitizen,
          secondCountryName: value?.secondCountryName,
          livingInAnotherCountry: value?.livingInAnotherCountry,
          secondLivingCountryName: value?.secondLivingCountryName,
          refusedFromAnyCountry: value?.refusedFromAnyCountry,
          refusedCountryName: value?.refusedCountryName,
          seriousMedicalCondition: value?.seriousMedicalCondition,
          medicalCause: value?.medicalCause,
          criminalOffence: value?.criminalOffence,
          causeOfCriminalOffence: value?.causeOfCriminalOffence,
          immigrationToOtherCountry: value?.immigrationToOtherCountry,
          immigratedCountry: value?.immigratedCountry,
        }));
        if (value?.academicInfo) setAcademicInfo(value.academicInfo);
        if (value?.testInfo) setTestInfo(value.testInfo);
        setWorkInfo(
          value?.workInfo?.length
            ? value.workInfo
            : [{ nameofOrg: null, from: null, to: null, docs: null, uid: crypto.randomUUID() }]
        );
        setAttachments(normalizeAdmissionAttachments(value));
        setOpenAdmissionTaskCount(Number(value?.openTaskCount ?? 0));
      })
      .catch(() => toast.error("Failed to load admission data."));
  }, [id, state, clientList, uniData, courseList]);

  // Re-resolve offering/fees when course catalog loads after the student record.
  useEffect(() => {
    if (!generalInfo?.course || !generalInfo?.university || !courseList?.length) return;
    if (generalInfo?.selectedUniversity?.onshoreFees?.length) return;

    setGeneralInfo((prev) => mergePlacementFields(prev, courseList));
  }, [
    courseList,
    generalInfo?.course,
    generalInfo?.university,
    generalInfo?.state,
    generalInfo?.fee,
    generalInfo?.selectedUniversity?.onshoreFees?.length,
  ]);

  useEffect(() => {
    const studentDbId = generalInfo?.id || id;
    if (!studentDbId || !generalInfo?.clientId) {
      setClientLocked(false);
      return;
    }
    let cancelled = false;
    fetchAdmissionAccountLinkExists(generalInfo.clientId, studentDbId).then((linked) => {
      if (!cancelled) setClientLocked(linked);
    });
    return () => {
      cancelled = true;
    };
  }, [generalInfo?.id, generalInfo?.clientId, id]);

  // The applicant summary and its completeness banner are a read-only mirror of the client
  // record, so read that record directly instead of the cached (and often not-yet-fetched)
  // client list — otherwise emergency contact and referral render as "missing" when they exist.
  useEffect(() => {
    const clientId = generalInfo?.clientId;
    if (!clientId || hydratedClientIdRef.current === clientId) return;
    hydratedClientIdRef.current = clientId;

    let cancelled = false;
    axios
      .get(`${API_URL}/clients/${clientId}`)
      .then(({ data }) => {
        const client = data?.data ?? data;
        if (cancelled || !client) return;
        setGeneralInfo((prev) => ({ ...prev, ...mapClientToProfileFields(client) }));
      })
      .catch((error) => {
        // Allow a retry on the next render pass rather than leaving the summary stale.
        if (hydratedClientIdRef.current === clientId) hydratedClientIdRef.current = null;
        console.warn(
          "Could not load client details for this admission:",
          error?.response?.data?.message ?? error?.message
        );
      });

    return () => {
      cancelled = true;
    };
  }, [generalInfo?.clientId]);

  const hasClient = Boolean(generalInfo?.clientId);

  const documentEmailUniversityId =
    Number(generalInfo?.selectedUniversity?.universityId ?? generalInfo?.university) || null;
  const documentEmailUniversity = useMemo(
    () =>
      documentEmailUniversityId
        ? uniData?.find((u) => Number(u?.id) === documentEmailUniversityId) ?? null
        : null,
    [uniData, documentEmailUniversityId]
  );

  const { isComplete, missing, missingBySection } = useMemo(
    () => getClientProfileCompleteness(hasClient ? generalInfo : null),
    [generalInfo, hasClient]
  );

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 0:
        return (
          <>
            <GeneralInfo
              generalInfo={generalInfo}
              setGeneralInfo={setGeneralInfo}
              clientLocked={clientLocked}
              admissionId={admissionId}
              onAdmissionCreated={(newId) => {
                setGeneralInfo((prev) => ({ ...prev, id: newId }));
                navigate(`/admin/admission/edit/${newId}`, { replace: true });
                setActiveTab(1);
              }}
              onSaved={() => setActiveTab(1)}
            />
          </>
        );
      case 1:
        return (
          <>
            <AdmissionAttachmentsTab
              attachments={attachments}
              setAttachments={setAttachments}
              admissionId={admissionId}
              universityId={documentEmailUniversityId ?? undefined}
              universityName={
                documentEmailUniversity?.name ?? generalInfo?.selectedUniversity?.universityName
              }
              universityEmail={documentEmailUniversity?.email}
              clientName={generalInfo?.name}
              clientStudentId={generalInfo?.studentId}
              clientEmail={generalInfo?.email}
              clientId={generalInfo?.clientId}
              clientPhone={generalInfo?.number}
              onSaveAndContinue={() => setActiveTab(2)}
              onSaveAndExit={() => navigate(-1)}
            />
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="h6" className="text-app-text font-semibold mb-1">
              Application form
            </Typography>
            <Typography variant="body2" color="text.secondary" className="mb-6 max-w-3xl">
              Follow the steps from placement through COE — save each section before moving on.
              General attachments are on the Attachments tab.
            </Typography>
            <UniversityInfo
              generalInfo={generalInfo}
              setGeneralInfo={setGeneralInfo}
              admissionId={admissionId}
              initialApplicationPhase={state?.applicationPhase}
              onAdmissionCreated={(newId) => {
                setGeneralInfo((prev) => ({ ...prev, id: newId }));
                navigate(`/admin/admission/edit/${newId}`, { replace: true });
              }}
            />
            {/* <DocumentsAndAddress
              addressInfo={addressInfo}
              setAddressInfo={setAddressInfo}
              generalInfo={generalInfo}
              hidePassportUpload
            /> */}
            {/* <TestInfo testInfo={testInfo} setTestInfo={setTestInfo} hideUploads />
            <WorkExperience workInfo={workInfo} setWorkInfo={setWorkInfo} />
            <AcademicInfo
              academicInfo={academicInfo}
              setAcademicInfo={setAcademicInfo}
              hideUploads
            /> */}
          </>
        );
      case 3:
        return (
          <AdmissionStatusHistory
            admissionId={admissionId}
            currentStatus={generalInfo?.status}
            onShoreStatus={generalInfo?.onShoreStatus}
            onStatusUpdated={(statusLabel) =>
              setGeneralInfo((prev) => ({ ...prev, status: statusLabel }))
            }
          />
        );
      case 4:
        return <AdmissionNotesTab admissionId={admissionId} />;
      case 5:
        return admissionId ? (
          <ModuleTasksTab
            entryType="ADMISSION"
            entryId={admissionId}
            clientId={generalInfo?.clientId}
            clientName={generalInfo?.name}
            taskModule="Admission"
            assignTitle={`Admission task — ${generalInfo?.name || "client"}`}
            onTasksChanged={refreshAdmissionTaskCount}
          />
        ) : null;
      default:
        return null;
    }
  }, [
    activeTab,
    generalInfo,
    academicInfo,
    addressInfo,
    testInfo,
    workInfo,
    attachments,
    admissionId,
    clientLocked,
    navigate,
    state?.applicationPhase,
    documentEmailUniversityId,
    documentEmailUniversity,
  ]);

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-app-surface overflow-hidden">
          <div className="rounded-t mb-0 px-4 md:px-8 py-4 border-b border-app-border bg-gradient-to-r from-app-surface-alt to-app-surface-muted">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="p-2 rounded-full hover:bg-app-surface-muted text-app-text-secondary"
                  onClick={() => navigate(-1)}
                  aria-label="Go back"
                >
                  <IoArrowBack className="text-xl" />
                </button>
                <div></div>
                <div>
                  <Typography variant="h5" className="font-semibold text-app-text">
                    {generalInfo?.name} - {id ? "Edit admission details" : "New admission"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage admission details across tabs — save when ready.
                  </Typography>
                </div>
              </div>
            </div>

            <Tabs
              value={activeTab}
              onChange={(_, value) => {
                const requiresAdmission = value === 1 || value === 2 || value === 5;
                if (requiresAdmission && !admissionId) {
                  toast.info("Select a client and create the application first");
                  return;
                }
                setActiveTab(value);
              }}
              variant="scrollable"
              scrollButtons="auto"
              className="mt-4"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": { textTransform: "none", fontWeight: 500, minHeight: 48 },
                "& .Mui-selected": { color: "#ea580c", fontWeight: 700 },
                "& .MuiTabs-indicator": { backgroundColor: "#ea580c" },
              }}
            >
              {TABS.map((tab, idx) => {
                const requiresAdmission = idx === 1 || idx === 2 || idx === 5;
                const isDisabled = requiresAdmission && !admissionId;
                const label =
                  tab.id === "applicant" && hasClient && !isComplete ? (
                    <Box
                      component="span"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                    >
                      {tab.label}
                      <WarningAmberOutlined sx={{ fontSize: 18, color: "warning.main" }} />
                    </Box>
                  ) : tab.id === "tasks" && openAdmissionTaskCount > 0 ? (
                    `Tasks (${openAdmissionTaskCount})`
                  ) : (
                    tab.label
                  );

                const tabEl = (
                  <Tab
                    key={tab.id}
                    value={idx}
                    label={label}
                    sx={isDisabled ? { opacity: 0.5, cursor: "default" } : undefined}
                  />
                );

                return isDisabled ? (
                  <Tooltip
                    key={tab.id}
                    title="Select a client and create the application first"
                    placement="bottom"
                  >
                    {tabEl}
                  </Tooltip>
                ) : (
                  tabEl
                );
              })}
            </Tabs>
          </div>

          <div className="flex flex-col min-h-[50vh]">
            <div className="flex-auto px-4 md:px-8 py-6 studentForm">{tabContent}</div>

            <div className="flex px-4 md:px-8 py-4 border-t border-app-border bg-app-surface-muted/90">
              <Button variant="text" color="inherit" onClick={() => navigate(-1)}>
                Exit
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudent;
